// ════════════════════════════════════════════════════════════════════════════
//  LPricing.jsx  ·  Dynamic Pricing — Powered by CRM v1.0
//
//  100% dinámico desde /api/public/plans
//  El CRM controla: precios, descuentos, badges, categorías, CTA, orden
//  Countdown live de descuentos con expiración
//  Responsive mobile-first
// ════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from "react";
import "./LPricing.css";

// ─────────────────────────────────────────────────────────────────────────────
//  CONFIG
// ─────────────────────────────────────────────────────────────────────────────
const API_BASE = import.meta.env.VITE_API_URL || "";

// ─────────────────────────────────────────────────────────────────────────────
//  HOOK: Fetch planes desde CRM
// ─────────────────────────────────────────────────────────────────────────────
function usePlansPublic() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    fetch(`${API_BASE}/api/public/plans`)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json) => {
        if (!cancelled) {
          setData(json);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.warn("[LPricing] API error, usando fallback:", err);
          setError(err.message);
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, []);

  return { data, loading, error };
}

// ─────────────────────────────────────────────────────────────────────────────
//  COUNTDOWN TIMER — días restantes de descuento
// ─────────────────────────────────────────────────────────────────────────────
function useCountdown(expiresStr) {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    if (!expiresStr) return;

    const tick = () => {
      const now  = new Date();
      const end  = new Date(expiresStr + "T23:59:59");
      const diff = end - now;
      if (diff <= 0) { setTimeLeft(null); return; }

      const days    = Math.floor(diff / 86400000);
      const hours   = Math.floor((diff % 86400000) / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeLeft({ days, hours, minutes, seconds, total: diff });
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [expiresStr]);

  return timeLeft;
}

// ─────────────────────────────────────────────────────────────────────────────
//  COUNTDOWN DISPLAY
// ─────────────────────────────────────────────────────────────────────────────
function CountdownBadge({ expires, daysLeft }) {
  const timeLeft = useCountdown(expires);

  if (!timeLeft && daysLeft === null) return null;

  const urgent = (daysLeft !== null && daysLeft <= 3) || (timeLeft && timeLeft.days <= 3);

  if (!timeLeft) {
    return (
      <span className={`lp-countdown ${urgent ? "lp-countdown--urgent" : ""}`}>
        {daysLeft === 0 ? "Termina HOY" : `${daysLeft}d restantes`}
      </span>
    );
  }

  if (timeLeft.days > 0) {
    return (
      <span className={`lp-countdown ${urgent ? "lp-countdown--urgent" : ""}`}>
        ⏱ {timeLeft.days}d {timeLeft.hours}h {timeLeft.minutes}m
      </span>
    );
  }

  return (
    <span className="lp-countdown lp-countdown--urgent">
      ⏱ {String(timeLeft.hours).padStart(2, "0")}:
      {String(timeLeft.minutes).padStart(2, "0")}:
      {String(timeLeft.seconds).padStart(2, "0")}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  HELPERS
// ─────────────────────────────────────────────────────────────────────────────
function fmt(n, currency = "MXN") {
  return Number(n || 0).toLocaleString("es-MX", {
    style: "currency", currency, minimumFractionDigits: 0,
  });
}

// Decide el estilo del botón CTA según cta_style del CRM
function ctaClass(plan) {
  if (plan.cta_style === "primary" || plan.badge === "MÁS POPULAR") return "cta-premium";
  if (plan.cta_style === "elite"   || plan.badge === "ELITE")        return "cta-tutor-pro";
  return "cta-free";
}

// ─────────────────────────────────────────────────────────────────────────────
//  PLAN CARD
// ─────────────────────────────────────────────────────────────────────────────
function PlanCard({ plan, onCta }) {
  const hasBadge    = plan.badge && plan.badge.trim() !== "";
  const hasDiscount = plan.discount_active;
  const isPopular   = plan.badge === "MÁS POPULAR";
  const isElite     = plan.badge === "ELITE";
  const isFeatured  = plan.featured || isPopular;

  const cardClass = [
    "pricing-card",
    isPopular ? "premium-card"    : "",
    isElite   ? "tutor-pro-card"  : "",
    isFeatured && !isPopular && !isElite ? "featured-card" : "",
  ].filter(Boolean).join(" ");

  return (
    <div className={cardClass} data-badge={plan.badge || ""}>
      {/* Badge superior (MÁS POPULAR / ELITE / custom) */}
      {hasBadge && (
        <div className={`lp-plan-badge ${isPopular ? "lp-plan-badge--popular" : isElite ? "lp-plan-badge--elite" : "lp-plan-badge--custom"}`}>
          {plan.badge}
        </div>
      )}

      {/* Descuento ribbon */}
      {hasDiscount && (
        <div className="lp-discount-ribbon">
          🏷️ -{plan.discount_pct}% · {plan.discount_label}
          {plan.discount_expires && (
            <CountdownBadge expires={plan.discount_expires} daysLeft={plan.discount_days_left} />
          )}
        </div>
      )}

      {/* Header del plan */}
      <div className="lp-plan-header">
        {plan.emoji && <span className="lp-plan-emoji">{plan.emoji}</span>}
        <h3 className="plan-name">{plan.name}</h3>
      </div>

      {/* Precio */}
      <div className="lp-price-block">
        {hasDiscount && (
          <div className="lp-price-original">
            <span className="lp-strikethrough">{fmt(plan.original_price, plan.currency)}</span>
            <span className="lp-discount-pct">-{plan.discount_pct}%</span>
          </div>
        )}
        <div className="plan-price" style={{ color: hasDiscount ? "var(--accent)" : undefined }}>
          {fmt(plan.price, plan.currency)}
        </div>
        <div className="plan-period">
          {plan.category === "package"
            ? `Paquete de ${plan.classes_per_month > 0 ? plan.classes_per_month : "?"} clases`
            : plan.price === 0
            ? "Para siempre"
            : "por mes"}
        </div>
        {hasDiscount && plan.price === 0 && (
          <div className="lp-free-tag">🎁 ¡Gratis con descuento!</div>
        )}
      </div>

      {/* Features */}
      <ul className="plan-features">
        {(plan.features || []).map((f, i) => {
          const isNegative = f.startsWith("❌") || f.startsWith("✗") || f.startsWith("No ");
          return (
            <li key={i} className={isNegative ? "lp-feat--no" : "lp-feat--yes"}>
              {!f.startsWith("✅") && !f.startsWith("❌") && (
                <span className="lp-feat-icon">{isNegative ? "❌" : "✅"}</span>
              )}
              {f.replace(/^[✅❌]\s*/, "")}
            </li>
          );
        })}
      </ul>

      {/* CTA */}
      <button
        className={`lp-cta-btn ${ctaClass(plan)}`}
        onClick={() => onCta && onCta(plan)}
        style={
          isFeatured && !isPopular && !isElite
            ? { background: plan.color, color: "#000", border: "none" }
            : undefined
        }
      >
        {plan.cta_text || "Empezar ahora"}
      </button>

      {/* Tokens info (si aplica) */}
      {plan.token_limit > 0 && (
        <div className="lp-tokens-info">
          🤖 {plan.token_limit.toLocaleString()} tokens IA/mes
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  SECTION HEADER
// ─────────────────────────────────────────────────────────────────────────────
function SectionHeader({ title, subtitle }) {
  return (
    <div className="lp-section-header">
      <h2 className="category-title">{title}</h2>
      {subtitle && <p className="lp-section-sub">{subtitle}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  GLOBAL PROMO BANNER — si HAY algún descuento activo
// ─────────────────────────────────────────────────────────────────────────────
function PromoBanner({ plans }) {
  const discounted = plans.filter((p) => p.discount_active);
  if (discounted.length === 0) return null;

  const biggest = discounted.reduce(
    (max, p) => (p.discount_pct > max.discount_pct ? p : max),
    discounted[0]
  );

  return (
    <div className="lp-promo-banner">
      <span className="lp-promo-fire">🔥</span>
      <span className="lp-promo-text">
        {biggest.discount_pct}% OFF en {biggest.name}
        {biggest.discount_label ? ` · ${biggest.discount_label}` : ""}
      </span>
      {biggest.discount_expires && (
        <CountdownBadge
          expires={biggest.discount_expires}
          daysLeft={biggest.discount_days_left}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  SKELETON LOADER
// ─────────────────────────────────────────────────────────────────────────────
function PricingSkeleton() {
  return (
    <div className="pricing-grid lp-skeleton-grid">
      {[1, 2, 3].map((i) => (
        <div key={i} className="pricing-card lp-skel-card">
          <div className="lp-skel lp-skel--title" />
          <div className="lp-skel lp-skel--price" />
          <div className="lp-skel lp-skel--feat" />
          <div className="lp-skel lp-skel--feat" />
          <div className="lp-skel lp-skel--feat" />
          <div className="lp-skel lp-skel--btn" />
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  FALLBACK ESTÁTICO — si la API falla o tarda
// ─────────────────────────────────────────────────────────────────────────────
const STATIC_FALLBACK = {
  subscriptions: [
    {
      id: "f1", name: "Free", price: 0, original_price: 0, currency: "MXN",
      token_limit: 100, classes_per_month: 0, category: "subscription",
      badge: "", featured: false, cta_text: "Empezar Gratis", cta_style: "default",
      emoji: "🌱",
      features: ["IA Limitada (3 conversaciones/semana)", "Dashboard básico", "Sistema de logros", "❌ Ruta MCERL", "❌ Clases grupales"],
      discount_active: false,
    },
    {
      id: "f2", name: "Premium 🔥", price: 29, original_price: 29, currency: "MXN",
      token_limit: 5000, classes_per_month: 0, category: "subscription",
      badge: "MÁS POPULAR", featured: true, cta_text: "Obtener Premium", cta_style: "primary",
      emoji: "⚡",
      features: ["IA Ilimitada 24/7", "Ruta MCERL Completa", "Clases GRUPALES en vivo", "Feedback detallado", "❌ Sesiones 1-a-1"],
      discount_active: false,
    },
    {
      id: "f3", name: "Tutor Pro 👑", price: 79, original_price: 79, currency: "MXN",
      token_limit: 10000, classes_per_month: 4, category: "subscription",
      badge: "ELITE", featured: false, cta_text: "Obtener Tutor Pro", cta_style: "elite",
      emoji: "💎",
      features: ["Todo de Premium", "4 sesiones 1-a-1 mensuales", "Profesor dedicado personal", "Plan personalizado", "Preparación IELTS/TOEFL"],
      discount_active: false,
    },
  ],
  packages: [
    {
      id: "p1", name: "Jumpstart", price: 800, original_price: 800, currency: "MXN",
      token_limit: 0, classes_per_month: 4, category: "package",
      badge: "", featured: false, cta_text: "Lo quiero", cta_style: "default",
      emoji: "🏃",
      features: ["4 clases con tutor", "1 clase por semana", "Flexibilidad en horario", "Aprendizaje personalizado"],
      discount_active: false,
    },
    {
      id: "p2", name: "Boost", price: 1400, original_price: 1400, currency: "MXN",
      token_limit: 0, classes_per_month: 8, category: "package",
      badge: "MÁS POPULAR", featured: true, cta_text: "Lo quiero", cta_style: "primary",
      emoji: "🚀",
      features: ["8 clases con tutor", "2 clases por semana", "Flexibilidad en horario", "Ahorras $200"],
      discount_active: false,
    },
    {
      id: "p3", name: "Speed", price: 1900, original_price: 1900, currency: "MXN",
      token_limit: 0, classes_per_month: 12, category: "package",
      badge: "ELITE", featured: false, cta_text: "Lo quiero", cta_style: "elite",
      emoji: "🏆",
      features: ["12 clases con tutor", "3 clases por semana", "Flexibilidad en horario", "Ahorras $500"],
      discount_active: false,
    },
  ],
};

// ─────────────────────────────────────────────────────────────────────────────
//  COMPONENTE RAÍZ
// ─────────────────────────────────────────────────────────────────────────────
export default function LPricing() {
  const { data, loading, error } = usePlansPublic();

  // Usa datos del CRM o el fallback estático
  const plans = data || STATIC_FALLBACK;
  const allPlans = plans.all || [...(plans.subscriptions || []), ...(plans.packages || [])];

  const handleCta = useCallback((plan) => {
    // Scroll al formulario de registro o link de pago
    const el = document.getElementById("register") || document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else window.location.href = `/register?plan=${plan.id}`;
  }, []);

  return (
    <section className="pricing-section" id="pricing">
      <div className="lp-container">

        {/* Header global */}
        <div className="pricing-header">
          <div className="pricing-urgency">⏰ Elige tu plan y empieza hoy</div>
        </div>

        {/* Banner de promo si hay descuentos */}
        {!loading && <PromoBanner plans={allPlans} />}

        {/* ── SUSCRIPCIONES ─────────────────────────────────────── */}
        {(plans.subscriptions?.length > 0 || loading) && (
          <div className="pricing-category">
            <SectionHeader
              title="Suscripciones Mensuales"
              subtitle="Acceso continuo con IA + Clases. Cancela cuando quieras."
            />
            {loading ? (
              <PricingSkeleton />
            ) : (
              <div className="pricing-grid">
                {plans.subscriptions.map((plan) => (
                  <PlanCard key={plan.id} plan={plan} onCta={handleCta} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── PAQUETES ──────────────────────────────────────────── */}
        {(plans.packages?.length > 0 || loading) && (
          <div className="pricing-category">
            <SectionHeader
              title="Paquetes de Clases con Tutor"
              subtitle="Paga una vez, úsalos cuando quieras. Sin mensualidad."
            />
            {loading ? (
              <PricingSkeleton />
            ) : (
              <div className="pricing-grid">
                {plans.packages.map((plan) => (
                  <PlanCard key={plan.id} plan={plan} onCta={handleCta} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── B2B ───────────────────────────────────────────────── */}
        {plans.b2b?.length > 0 && (
          <div className="pricing-category lp-b2b-section">
            <SectionHeader
              title="Para Empresas"
              subtitle="Planes personalizados para equipos. Precio por contrato."
            />
            <div className="lp-b2b-card">
              <span className="lp-b2b-icon">🏢</span>
              <div className="lp-b2b-body">
                <h3>Capacita a tu equipo en inglés</h3>
                <p>Dashboard empresa · IA ilimitada · Grupos corporativos · Reporte de avance</p>
              </div>
              <button className="cta-tutor-pro lp-b2b-btn" onClick={() => {
                document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
              }}>
                Solicitar Cotización
              </button>
            </div>
          </div>
        )}

        {/* Error subtle (solo en dev) */}
        {error && process.env.NODE_ENV === "development" && (
          <p style={{ textAlign: "center", color: "#888", fontSize: 11, marginTop: 8 }}>
            [dev] API error: {error} — usando datos estáticos
          </p>
        )}

      </div>
    </section>
  );
}